import { CascadeList, Popover, usePopoverHeight } from '@byecode/ui'
import type { CascadeFilterConfig } from '@lighthouse/core'
import { type FilterBlockItemConfig, type FilterOption } from '@lighthouse/core'
import { CheckButton } from '@lighthouse/shared'
import { intersection } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const SCxTileFilterList = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
`
interface TileCascadeFilterItemProps {
    config: FilterBlockItemConfig & CascadeFilterConfig
    option: FilterOption
    value: string[]
    onChange: (val: string[]) => void
}

const TileCascadeFilterItem: React.FC<TileCascadeFilterItemProps> = ({ config, option, value, onChange }) => {
    const [open, setOpen] = useState(false)

    const { ref, height: maxHeight } = usePopoverHeight({ opened: open, popoverMaxHeight: 560 })

    const handleSelect = useCallback(
        (v: string) => {
            if (value.includes(v)) {
                const newVal = value.filter(d => d !== v)
                // setVal(newVal)
                onChange(newVal)
                return
            }
            onChange([v])
        },
        [onChange, value]
    )

    const handleChange = useCallback(
        (v: string[]) => {
            // @柯霖 取交集会导致新选择的选项无法选中
            // const realVal = intersection(value, v)
            onChange(v)
            config?.isLastLevel && setOpen(false)
        },
        [config?.isLastLevel, onChange]
    )

    const getFlatOptions = useCallback((opts: FilterOption[] = []) => {
        return opts.reduce<FilterOption[]>((prev, cur) => {
            prev.push({
                label: cur.label,
                value: cur.value
            })
            if (cur.children) {
                prev = [...prev, ...getFlatOptions(cur.children)]
            }
            return prev
        }, [])
    }, [])

    const isActive = useMemo(() => {
        const opts = [{ label: option.label, value: option.value }, ...getFlatOptions(option.children)]
        const values = opts.map(item => item.value)
        const selectValues = intersection(values, value)
        return selectValues.length > 0
    }, [getFlatOptions, option.children, option.label, option.value, value])

    return useMemo(() => {
        if (!option?.children || option.children.length === 0) {
            return <CheckButton key={option.value} label={option.label} active={isActive} onClick={() => handleSelect(option.value)} />
        }
        return (
            <Popover opened={open} onChange={setOpen} withinPortal width="auto">
                <Popover.Target>
                    <div>
                        <CheckButton key={option.value} label={option.label} active={isActive} isSuffixIcon ref={ref} />
                    </div>
                </Popover.Target>
                <Popover.Dropdown compact>
                    <CascadeList
                        data={value}
                        onChange={handleChange}
                        allSelected
                        multiple={!config?.isLastLevel}
                        selectionMode={config.isLastLevel ? 'leaf-only' : 'auto-leaf'}
                        options={option.children}
                        style={{ maxHeight }}
                    />
                </Popover.Dropdown>
            </Popover>
        )
    }, [config, handleChange, handleSelect, isActive, maxHeight, open, option.children, option.label, option.value, ref, value])
}

interface TileCascadeFilterProps extends Pick<TileCascadeFilterItemProps, 'config' | 'value'> {
    noFilterLabel?: string
    options: FilterOption[]
    onClear?: () => void
    onChange: (val: string[]) => void
}

export const TileCascadeFilter: React.FC<TileCascadeFilterProps> = ({ config, value, noFilterLabel, options, onChange, onClear }) => {
    return (
        <SCxTileFilterList>
            {options.length > 0 && <CheckButton label={noFilterLabel || '全部'} active={!value || value.length === 0} onClick={onClear} />}
            {options.map(option => {
                const { label, value: itemValue, children } = option
                // const childValues = [{ label, value: itemValue }, ...getFlatOptions(children || [])].map(item => item.value)
                // const realValue = intersection(childValues, value)
                return <TileCascadeFilterItem key={itemValue} option={option} value={value} config={config} onChange={onChange} />
            })}
        </SCxTileFilterList>
    )
}
